import { useEffect, useState } from 'react';
import { useAppSelector } from '../store/hooks';
import { getExportRequests, requestExport, type GDPRExportRequest, type ExportFormat } from '../api/privacy';
import { getMyOrganisations, type OrgMembership } from '../api/org';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import ExportProgressModal from '../components/jobs/ExportProgressModal';
import { downloadPortfolioPDF } from '../api/portfolio-pdf';
import { downloadCsvFiling } from '../api/tax-filing';

export default function DataExportPage() {
  const userId = useAppSelector((s) => s.auth.user?.id);
  const tier = useAppSelector((s) => s.auth.user?.tier as string | undefined);
  type ExportType = ExportFormat | 'PDF';
  const [format, setFormat] = useState<ExportType>('JSON');
  const [loading, setLoading] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requests, setRequests] = useState<GDPRExportRequest[]>([]);
  const [exportJobId, setExportJobId] = useState<string | null>(null);

  // Org-level export state
  const [orgs, setOrgs] = useState<OrgMembership[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [orgExportLoading, setOrgExportLoading] = useState(false);
  const [orgExportStatus, setOrgExportStatus] = useState<string | null>(null);

  // PDF export state (Chunk 94)
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfStatus, setPdfStatus] = useState<string | null>(null);

  // CoinTracker / Koinly CSV export state (Chunk 126)
  const [csvExportLoading, setCsvExportLoading] = useState<string | null>(null);
  const [csvExportStatus, setCsvExportStatus] = useState<string | null>(null);
  const [csvExportYear, setCsvExportYear] = useState<number>(new Date().getFullYear() - 1);

  const isAdminInOrg = orgs.some((o) =>
    ['owner', 'admin'].includes(o.yourRole) && o.id === selectedOrgId,
  );

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const result = await getExportRequests(userId);
      setRequests(result.requests);
    } finally {
      setLoading(false);
    }
  };

  const loadOrgs = async () => {
    if (!tier || !['firm', 'enterprise', 'institutional'].includes(tier)) return;
    try {
      const res = await getMyOrganisations();
      setOrgs(res.organisations);
      if (res.organisations.length > 0) {
        setSelectedOrgId(res.organisations[0].id);
      }
    } catch {
      // Not a firm user — ignore
    }
  };

  useEffect(() => {
    void load();
    void loadOrgs();
  }, [userId, tier]);

  const handleExportClick = async () => {
    if (!userId) return;
    setRequesting(true);
    try {
      const gdprFormat: ExportFormat = format === 'PDF' ? 'JSON' : format;
      const response = await requestExport(userId, gdprFormat);
      if (response.id) {
        setExportJobId(response.id);
      }
      await load();
    } finally {
      setRequesting(false);
    }
  };

  const handleOrgExport = async () => {
    if (!selectedOrgId) return;
    setOrgExportLoading(true);
    setOrgExportStatus(null);
    try {
      // Org CSV export: download transactions for all org members
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3000/api'}/auth/org/${selectedOrgId}/export?format=${format}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken') || ''}`,
          },
        },
      );
      if (!res.ok) {
        const errData = await res.json() as { error?: string };
        setOrgExportStatus(`Export failed: ${errData.error ?? 'Unknown error'}`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `org-export-${selectedOrgId.slice(0, 8)}.${(format === 'PDF' ? 'json' : format.toLowerCase())}`;
      a.click();
      URL.revokeObjectURL(url);
      setOrgExportStatus('Export downloaded successfully.');
    } catch {
      setOrgExportStatus('Export failed — please try again.');
    } finally {
      setOrgExportLoading(false);
    }
  };

  const handlePdfExport = async () => {
    if (!userId) return;
    setPdfLoading(true);
    setPdfStatus(null);
    try {
      await downloadPortfolioPDF(userId);
      setPdfStatus('PDF downloaded successfully.');
    } catch {
      setPdfStatus('PDF generation failed — try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleCsvExport = async (platform: 'cointracker' | 'koinly') => {
    if (!userId) return;
    setCsvExportLoading(platform);
    setCsvExportStatus(null);
    try {
      const blob = await downloadCsvFiling(userId, { taxYear: csvExportYear, provider: 'csv', platform });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${platform}_export_${csvExportYear}.csv`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
      setCsvExportStatus(`${platform === 'cointracker' ? 'CoinTracker' : 'Koinly'} CSV downloaded successfully.`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Export failed';
      setCsvExportStatus(`Error: ${msg}`);
    } finally {
      setCsvExportLoading(null);
    }
  };

  if (!userId) return <div className="p-6 text-sm text-text-muted">Please log in to request a data export.</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Data Export</h1>
        <p className="text-sm text-text-secondary">Request GDPR-compliant exports of your account data.</p>
      </div>

      <Card>
        <div className="flex items-center gap-3">
          <select value={format} onChange={(e) => setFormat(e.target.value as ExportFormat)} className="rounded-lg border border-border bg-surface-active px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/50">
            <option value="JSON">JSON</option>
            <option value="CSV">CSV</option>
            <option value="PDF">Portfolio Performance Report (PDF)</option>
          </select>
          {format === 'PDF' ? (
            <Button
              size="sm"
              isLoading={pdfLoading}
              onClick={() => { void handlePdfExport(); }}
            >
              Download PDF
            </Button>
          ) : (
            <Button
              size="sm"
              isLoading={requesting}
              onClick={() => { void handleExportClick(); }}
            >
              Request Export
            </Button>
          )}
        </div>
        {pdfStatus && (
          <p className={`text-sm mt-2 ${pdfStatus.includes('failed') ? 'text-red-400' : 'text-emerald-400'}`}>
            {pdfStatus}
          </p>
        )}
      </Card>

      {/* Org-level export — only shown to firm/enterprise admins */}
      {orgs.length > 0 && (
        <Card title="Organisation Export">
          <p className="text-sm text-text-secondary mb-4">
            Export all member transactions in one file. Available to organisation admins.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {orgs.length > 1 && (
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                className="rounded-lg border border-border bg-surface-active px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              >
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name} ({o.yourRole})
                  </option>
                ))}
              </select>
            )}
            <select
              value={format === 'PDF' ? 'JSON' : format}
              onChange={(e) => setFormat(e.target.value as ExportFormat)}
              className="rounded-lg border border-border bg-surface-active px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/50"
            >
              <option value="JSON">JSON</option>
              <option value="CSV">CSV</option>
            </select>
            <Button
              size="sm"
              isLoading={orgExportLoading}
              disabled={!isAdminInOrg}
              onClick={() => { void handleOrgExport(); }}
            >
              Export Org Data
            </Button>
          </div>
          {!isAdminInOrg && selectedOrgId && (
            <p className="text-xs text-text-muted mt-2">
              Only organisation admins and owners can export org data.
            </p>
          )}
          {orgExportStatus && (
            <p className="text-sm text-text-secondary mt-2">{orgExportStatus}</p>
          )}
        </Card>
      )}

      <Card title="Export Requests">
        {loading ? <p className="text-sm text-text-muted">Loading...</p> : null}
        <div className="space-y-3">
          {requests.map((req) => (
            <div key={req.id} className="rounded-lg border border-border bg-surface-hover p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-semibold text-text-primary text-sm">{req.exportFormat}</span>
                <Badge variant={req.status === 'COMPLETED' ? 'success' : req.status === 'FAILED' ? 'danger' : 'warning'}>{req.status}</Badge>
              </div>
              <p className="text-text-muted text-xs font-mono">Requested: {new Date(req.requestedAt).toLocaleString()}</p>
              {req.downloadUrl ? <a className="text-primary-400 hover:text-primary-300 underline text-sm mt-1 inline-block" href={req.downloadUrl}>Download</a> : null}
              {(req.status === 'PENDING' || req.status === 'PROCESSING') && (
                <button
                  onClick={() => setExportJobId(req.id)}
                  className="text-primary-400 hover:text-primary-300 underline text-xs mt-1 block"
                >
                  View Progress
                </button>
              )}
            </div>
          ))}
          {requests.length === 0 && !loading ? <p className="text-sm text-text-muted">No export requests yet.</p> : null}
        </div>
      </Card>

      {exportJobId ? (
        <ExportProgressModal
          jobId={exportJobId}
          title={`${format} Export Progress`}
          isOpen={!!exportJobId}
          onClose={() => {
            setExportJobId(null);
            void load();
          }}
        />
      ) : null}

      {/* ── Tax Platform CSV Exports (Chunk 126) ─────────────────────────── */}
      <Card>
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-text-primary">Tax Platform CSV Exports</h2>
            <p className="text-sm text-text-secondary mt-0.5">
              Download pre-formatted CSV files compatible with CoinTracker and Koinly. No account required — available to all tiers.
            </p>
          </div>

          {/* Year selector */}
          <div className="flex items-center gap-3">
            <label className="text-sm text-text-secondary">Tax Year:</label>
            <select
              value={csvExportYear}
              onChange={(e) => setCsvExportYear(Number(e.target.value))}
              className="rounded-lg border border-border bg-surface-active px-3 py-1.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/50"
            >
              {[2022, 2023, 2024, 2025].map((yr) => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>

          {/* Export buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* CoinTracker */}
            <div className="rounded-xl border border-border bg-surface p-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-text-primary">CoinTracker CSV</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  Columns: Date · Received Quantity · Received Currency · Sent Quantity · Sent Currency · Fee Amount · Fee Currency · Tag
                </p>
              </div>
              <div className="text-xs text-text-muted space-y-0.5">
                <p><span className="font-medium text-text-secondary">Date</span> — ISO 8601 format (YYYY-MM-DD HH:mm:ss)</p>
                <p><span className="font-medium text-text-secondary">Quantity</span> — Raw token amounts (not USD)</p>
                <p><span className="font-medium text-text-secondary">Tag</span> — trade, transfer, income, spend</p>
              </div>
              <Button
                size="sm"
                isLoading={csvExportLoading === 'cointracker'}
                onClick={() => void handleCsvExport('cointracker')}
              >
                Download CoinTracker CSV
              </Button>
            </div>

            {/* Koinly */}
            <div className="rounded-xl border border-border bg-surface p-4 space-y-3">
              <div>
                <p className="text-sm font-semibold text-text-primary">Koinly CSV</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  Columns: Date · Sent Amount · Sent Currency · Received Amount · Received Currency · Fee Amount · Fee Currency · Net Worth Amount · Net Worth Currency · Label · Description · TxHash
                </p>
              </div>
              <div className="text-xs text-text-muted space-y-0.5">
                <p><span className="font-medium text-text-secondary">Date</span> — UTC timestamp</p>
                <p><span className="font-medium text-text-secondary">Net Worth</span> — Left blank; Koinly fills from market data</p>
                <p><span className="font-medium text-text-secondary">Label</span> — Maps to transaction tag/category</p>
              </div>
              <Button
                size="sm"
                isLoading={csvExportLoading === 'koinly'}
                onClick={() => void handleCsvExport('koinly')}
              >
                Download Koinly CSV
              </Button>
            </div>
          </div>

          {/* Status */}
          {csvExportStatus && (
            <p className={`text-sm ${csvExportStatus.startsWith('Error') ? 'text-red-400' : 'text-emerald-400'}`}>
              {csvExportStatus}
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}
